import type { BrandSearch } from '#shared/interfaces/brands'
import type { IdName } from '@/interfaces/global'
import type { BodyTypesResponse } from '#shared/interfaces/top/bodyTypes'
import type { UsedCarsSearch } from '#shared/interfaces/usedCar'
import { postWindowBridge } from '@/utils/windowBridge'
import { jsonParse } from '#shared/utils/jsonParse'
import { LOG_SOURCES } from '#shared/constant/log'
import type { ReceiveUserData } from '@/interfaces/receiveUserEvent'
import { BFF_FETCH_RETRY_COUNT } from '@/constant/fetch'

// url_scheme は `window.open(`${urlScheme}://...`)` の組み立てに使うため、
// RFC 3986 のスキーム形式に合わない値は格納しない（不正値のログは出さない）
const URL_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z0-9+.-]*$/

type ReceiveUserEventListener = (
  userData: ReceiveUserData
) => void | Promise<void>

export default defineNuxtPlugin(() => {
  const urlSchemeState = useState<string>('url_schema', () => '')
  const receiveUserEventListeners = new Set<ReceiveUserEventListener>()

  // receive-user-event の受信データを ReceiveUserData に変換する。
  // パース失敗・想定外の形状の場合は null を返す（パース失敗は jsonParse がログを出す）。
  const parseReceiveUserEvent = (event: Event): ReceiveUserData | null => {
    if (!('detail' in event)) return null

    // detail は null の可能性があるため optional chaining で参照する
    const rawData: unknown = (event.detail as { data?: unknown } | null)?.data
    if (typeof rawData !== 'string') return null

    // 発火元が JSON.stringify(arguments) で包むため、2 回剥がすと目的のオブジェクトになる。
    // jsonParse の戻り値は any のため、null 判定に必要な null を含めて型注記する
    const parsedArrayData: string[] | null = jsonParse(
      rawData,
      LOG_SOURCES.RECEIVE_USER_DATA
    )
    if (parsedArrayData === null) return null

    // 先頭要素が undefined の場合も jsonParse がパース失敗として null を返す
    const parsedUserData: ReceiveUserData | null = jsonParse(
      parsedArrayData[0],
      LOG_SOURCES.RECEIVE_USER_DATA
    )
    if (parsedUserData === null) return null

    // 型注記は実行時には何も保証しないため、項目ごとに typeof で確認する
    const { url_scheme, token } = parsedUserData
    return {
      url_scheme:
        typeof url_scheme === 'string' && URL_SCHEME_PATTERN.test(url_scheme)
          ? url_scheme
          : undefined,
      token: typeof token === 'string' ? token : undefined,
    }
  }

  // 購読者は受信のたびに必ず呼ぶ。お気に入りの保留操作はトークンの到着自体をトリガにしており、
  // 再送されたトークンが前回と同値でも実行する必要があるため、値で間引いてはならない
  const onReceiveUserEventPlugin = (event: Event): void => {
    const userData = parseReceiveUserEvent(event)
    if (userData === null) return

    if (userData.url_scheme) {
      urlSchemeState.value = userData.url_scheme
    }
    receiveUserEventListeners.forEach((listener) => {
      listener(userData)
    })
  }
  // plugin は 1 ドキュメントにつき 1 回だけ実行され、リスナーの寿命が document と一致するため解除しない
  if (import.meta.client) {
    window.addEventListener('receive-user-event', onReceiveUserEventPlugin)
  }
  return {
    provide: {
      // receive-user-event の受信データを購読する。戻り値の関数で購読を解除する。
      subscribeReceiveUserEvent(
        listener: ReceiveUserEventListener
      ): () => void {
        receiveUserEventListeners.add(listener)
        return (): void => {
          receiveUserEventListeners.delete(listener)
        }
      },
      checkText(text?: string | number) {
        if (text) {
          if (text === '****' || text === '99999' || text === 99999) {
            return '-'
          }
          return text
        }
        return '-'
      },
      // provide する関数は呼び出し位置が呼び出し側に委ねられるため $fetch を使う
      async getBrands(): Promise<IdName[]> {
        const returnIdNames: IdName[] = []
        const brandDatas = await $fetch<BrandSearch>('/api/brand/selection', {
          cache: 'no-cache',
          retry: BFF_FETCH_RETRY_COUNT,
          params: {
            _fields: 'data,country_cd,country_name,brands,brand_cd,brand_name',
            order: 'designated_sort',
          },
        })
        if (brandDatas && brandDatas.data) {
          brandDatas.data.forEach((brandData) => {
            brandData.brands.forEach((brandDetail) => {
              returnIdNames.push({
                id: brandDetail.brand_cd.toString(),
                name: brandDetail.brand_name,
              })
            })
          })
        }
        return returnIdNames
      },
      async getBodyTypes(): Promise<IdName[]> {
        const returnIdNames: IdName[] = []
        const bodyTypeDatas = await $fetch<BodyTypesResponse>(
          '/api/catalogs/bodyTypes',
          {
            cache: 'no-cache',
            retry: BFF_FETCH_RETRY_COUNT,
          }
        )
        if (bodyTypeDatas && bodyTypeDatas.data) {
          bodyTypeDatas.data.forEach((bodyTypeData) => {
            bodyTypeData.items.forEach((bodyTypeDetail) => {
              returnIdNames.push({
                id: bodyTypeDetail.body_sub_cd,
                name:
                  bodyTypeDetail.body_sub_cd === '018'
                    ? bodyTypeDetail.body_sub_name.replace(
                        'ハッチバック',
                        'コンパクトカー'
                      )
                    : bodyTypeDetail.body_sub_name,
              })
            })
          })
        }
        return returnIdNames
      },
      createDisplayCarPrice(
        lowerPrice?: string | number,
        upperPrice?: string | number
      ): (string | number)[] {
        // 1. 両方とも値がある場合
        if (lowerPrice && upperPrice) {
          return lowerPrice === upperPrice
            ? [lowerPrice]
            : [lowerPrice, upperPrice]
        }

        // 2. lowerPrice だけがある場合
        if (lowerPrice) {
          return [lowerPrice]
        }

        // 3. upperPrice だけがある場合
        if (upperPrice) {
          return [upperPrice]
        }

        // 4. 両方ともない場合
        return ['ーー']
      },
      changeMissionCd(searchMissionCd: number) {
        switch (searchMissionCd) {
          case 101:
            return ['1']
          case 120:
            return ['1', '2']
          case 130:
            return ['1', '3']
          case 140:
            return ['1', '4']
          case 150:
            return ['1', '1']
          case 201:
            return ['2']
          default:
            return
        }
      },
      // ネイティブアプリ側が parameters フィールドの存在を前提としているため、parameters 未指定時でも空オブジェクトを送信する
      sendEventGaTag(
        event: string,
        parameters: Record<string, string | number | undefined> = {}
      ) {
        postWindowBridge(
          'gtagApp',
          JSON.stringify({ Event: event, parameters })
        )
      },
      convertCarCode(carCd?: string) {
        // 統合車種一覧
        const convertMap = new Map([
          ['55241512', '55241501'],
          ['55241518', '55241501'],
          ['55241514', '55241502'],
          ['55241508', '55241502'],
          ['55241507', '55241502'],
          ['55241513', '55241502'],
          ['55242517', '55242518'],
          ['55241516', '55241515'],
          ['55241521', '55241522'],
          ['55241504', '55241503'],
          ['55241511', '55241503'],
          ['55241510', '55241503'],
          ['55242506', '55242501'],
          ['55242504', '55242501'],
          ['55242502', '55242507'],
          ['55242503', '55242507'],
          ['55242505', '55242507'],
          ['55242509', '55242508'],
          ['55241509', '55241505'],
          ['55241506', '55241505'],
          ['55201525', '55201524'],
          ['55202509', '55201524'],
          ['55152510', '55152509'],
          ['25502505', '25502502'],
          ['25502507', '25502504'],
          ['20253501', '20253503'],
          ['20351508', '20351503'],
          ['20352501', '20352514'],
          ['20352504', '20352513'],
          ['20352503', '20352513'],
          ['10202502', '10202009'],
          ['10202503', '10202009'],
          ['10252501', '10252004'],
          ['10251501', '10251004'],
          ['10251503', '10251018'],
          ['10253502', '10253501'],
          ['10251505', '10251017'],
          ['10252023', '10252022'],
          ['10252025', '10251032'],
          ['10401505', '10401001'],
          ['10152023', '10152033'],
          ['10151507', '10151003'],
          ['10151508', '10151003'],
          ['10152502', '10152012'],
          ['10152024', '10152033'],
          ['10451001', '10451002'],
          ['10552032', '10552031'],
          ['10552037', '10552031'],
          ['10102501', '10102065'],
          ['10102502', '10102065'],
          ['10102050', '10102070'],
          ['10102051', '10102070'],
          ['10102039', '10102010'],
          ['10102038', '10102010'],
          ['10101045', '10101046'],
          ['10102057', '10102044'],
        ])
        if (carCd && convertMap.get(carCd)) {
          return convertMap.get(carCd)
        } else {
          return carCd
        }
      },
      async fetchUsedCars(
        params: Record<string, string | number>
      ): Promise<UsedCarsSearch | undefined> {
        // BFF が 204 を返した場合は undefined になる
        const result = await $fetch<UsedCarsSearch | undefined>(
          '/api/usedCars',
          {
            cache: 'no-cache',
            retry: BFF_FETCH_RETRY_COUNT,
            params,
          }
        )
        return result
      },
      async getSchemaUrl(urlPath: string): Promise<string> {
        if (urlSchemeState.value) {
          return urlSchemeState.value + urlPath
        }
        return new Promise((resolve, reject) => {
          const timeoutId = setTimeout(() => {
            if (!urlSchemeState.value) {
              unwatch()
              reject()
            }
          }, 5000)

          const unwatch = watch(urlSchemeState, (newValue) => {
            if (newValue) {
              unwatch()
              clearTimeout(timeoutId)
              resolve(newValue + urlPath)
            }
          })

          if (import.meta.client) {
            postWindowBridge('getUserDataFromApp', true)
          } else {
            unwatch()
            clearTimeout(timeoutId)
            reject()
          }
        })
      },
    },
  }
})
